'use client'

import type { GrowthBook } from '@growthbook/growthbook'
import {
  GrowthBookProvider,
  useFeatureIsOn as useSdkFeatureIsOn,
  useFeatureValue as useSdkFeatureValue,
  useGrowthBook,
} from '@growthbook/growthbook-react'
import type { ReactNode } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { DEFAULT_LOAD_TIMEOUT_MS } from './constants'
import { createGrowthBook, loadFeatures, resolveAttributes } from './sdk'
import { createExposureTracker, type ExposureTracker } from './tracking'
import type { FeatureFlagsConfig, FlagValue, WidenFlagValue } from './types'

interface ReadinessContextValue {
  /** The provider buckets experiments, so readiness waits for `id`. */
  expectsIdentity: boolean
  /** The client holds the attributes of the latest render. */
  attributesApplied: boolean
}

const ReadinessContext = createContext<ReadinessContextValue>({
  expectsIdentity: false,
  attributesApplied: true,
})

export interface FeatureFlagsProviderProps extends FeatureFlagsConfig {
  children: ReactNode
}

/**
 * The mount's own client, built with its attributes and destroyed on unmount.
 * StrictMode runs the cleanup and then the effect again on the same client,
 * so a destroyed one is replaced rather than reused.
 */
function useOwnedGrowthBook(
  config: FeatureFlagsConfig,
  tracker: ExposureTracker,
): GrowthBook {
  const create = () => createGrowthBook(config, tracker.callback)
  const [growthbook, setGrowthbook] = useState(create)

  useEffect(() => {
    if (growthbook.isDestroyed()) {
      setGrowthbook(create())
      return
    }
    void loadFeatures(growthbook, { streaming: config.streaming })
    return () => growthbook.destroy()
  }, [growthbook])

  return growthbook
}

/**
 * Applies changed `attributes` / `identity` and reports whether the client
 * holds the latest ones. The client is built with the first render's, so
 * that render counts as applied.
 */
function useAppliedAttributes(
  growthbook: GrowthBook,
  {
    applicationId,
    attributes,
    identity,
  }: Pick<FeatureFlagsConfig, 'applicationId' | 'attributes' | 'identity'>,
): boolean {
  // Keyed on the values: consumers pass a new object each render.
  const key = JSON.stringify([
    applicationId,
    attributes ?? {},
    identity ?? null,
  ])
  const [appliedKey, setAppliedKey] = useState(key)
  // What the client was last given, not what it confirmed: compared with
  // `appliedKey`, a change that reverts before the previous one settles
  // (A → B → A) would never re-send A and leave the client on B. Cleared on
  // cleanup, so a send interrupted before it settled is sent again.
  const sentKey = useRef<string | null>(key)

  useEffect(() => {
    if (sentKey.current === key) return
    sentKey.current = key
    let current = true
    void growthbook
      .setAttributes(resolveAttributes({ applicationId, attributes, identity }))
      .then(() => {
        if (current) setAppliedKey(key)
      })
    return () => {
      current = false
      sentKey.current = null
    }
  }, [growthbook, applicationId, key])

  return appliedKey === key
}

/**
 * Provides flags to its subtree and never blocks render: children mount
 * immediately and read defaults until the payload lands. Wrap them in
 * `FeatureFlagsReady` to hold a fallback instead.
 *
 * Each mount owns its client, built with the mount's attributes and destroyed
 * on unmount, so a remount (a panel reopened for another product) never sees
 * an earlier mount's attributes and nothing else on the page is affected.
 * `clientKey`, `environment` and `streaming` are read when the client is
 * created; `attributes`, `identity` and `onExperimentViewed` may change at any
 * time. Exposures evaluated before `onExperimentViewed` is set are delivered
 * once it is.
 *
 * For experiments pass `identity={useSegmentIdentity()}` and wire
 * `onExperimentViewed` to the app's Avo `experimentViewed`.
 */
export function FeatureFlagsProvider({
  children,
  ...config
}: Readonly<FeatureFlagsProviderProps>) {
  const { applicationId, identity, onExperimentViewed } = config
  const [tracker] = useState(() => createExposureTracker(applicationId))
  useEffect(() => {
    tracker.setHandler(onExperimentViewed)
  }, [tracker, onExperimentViewed])

  const growthbook = useOwnedGrowthBook(config, tracker)
  const attributesApplied = useAppliedAttributes(growthbook, config)
  const expectsIdentity = identity !== undefined
  const readiness = useMemo(
    () => ({ expectsIdentity, attributesApplied }),
    [expectsIdentity, attributesApplied],
  )

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <ReadinessContext.Provider value={readiness}>
        {children}
      </ReadinessContext.Provider>
    </GrowthBookProvider>
  )
}

/**
 * Whether flags can be trusted: the payload has loaded (or failed), the
 * client holds the current attributes and, for an app running experiments,
 * the `id` has reached it — reading earlier serves the fallback rule or stale
 * targeting and flips a tick later. Must be rendered inside
 * `FeatureFlagsProvider`.
 */
export const useFeatureFlagsReady = (): boolean => {
  const growthbook = useGrowthBook()
  const { expectsIdentity, attributesApplied } = useContext(ReadinessContext)
  return (
    growthbook.ready &&
    attributesApplied &&
    (!expectsIdentity || 'id' in growthbook.getAttributes())
  )
}

export interface FeatureFlagsReadyProps {
  children: ReactNode
  fallback?: ReactNode
  /** Render children with defaults after this long even if flags are not ready. */
  timeoutMs?: number
}

/**
 * Renders `fallback` until `useFeatureFlagsReady()` (or `timeoutMs` passes).
 * Once shown, children stay mounted: a later attribute change must not
 * unmount the subtree for a tick.
 */
export function FeatureFlagsReady({
  children,
  fallback = null,
  timeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
}: Readonly<FeatureFlagsReadyProps>) {
  const ready = useFeatureFlagsReady()
  const [timedOut, setTimedOut] = useState(false)
  const [released, setReleased] = useState(false)
  const show = released || ready || timedOut
  if (show && !released) setReleased(true)

  useEffect(() => {
    if (show) return
    const timer = setTimeout(() => setTimedOut(true), timeoutMs)
    return () => clearTimeout(timer)
  }, [show, timeoutMs])

  return <>{show ? children : fallback}</>
}

/**
 * Must be rendered inside `FeatureFlagsProvider` (throws otherwise). A read
 * counts as an exposure, so read an experiment flag where its variant shows.
 */
export const useFeatureIsOn = (key: string): boolean => useSdkFeatureIsOn(key)

/** Must be rendered inside `FeatureFlagsProvider` (throws otherwise). */
export const useFeatureValue = <T extends FlagValue>(
  key: string,
  fallback: T,
): WidenFlagValue<T> => useSdkFeatureValue(key, fallback) as WidenFlagValue<T>
